import {
  GRAPH_VIEW_STATE_SCHEMA_VERSION,
  parseSavedView,
  type CaptureCurrentViewResult,
  type ParsedSavedGraphView,
} from '@/helpers/graphViewState.helpers';

export const REMAP_SESSION_CACHE_VERSION = 1;
export const REMAP_SESSION_CACHE_PREFIX = 'review-ui:remap-session:v1:';

interface ReMapSessionCacheEnvelope {
  version: number;
  projectId: string;
  view: Pick<
    CaptureCurrentViewResult,
    'view_mode' | 'client_metadata' | 'schema_version'
  >;
}

const availableStorage = (storage?: Storage): Storage | null => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const reMapSessionCacheKey = (projectId: string): string =>
  `${REMAP_SESSION_CACHE_PREFIX}${encodeURIComponent(projectId)}`;

export function writeReMapSessionCache(
  projectId: string,
  captured: CaptureCurrentViewResult,
  storage?: Storage,
): boolean {
  const target = availableStorage(storage);
  if (!target || !projectId) return false;
  const envelope: ReMapSessionCacheEnvelope = {
    version: REMAP_SESSION_CACHE_VERSION,
    projectId,
    view: {
      view_mode: captured.view_mode,
      client_metadata: captured.client_metadata,
      schema_version: captured.schema_version,
    },
  };
  try {
    target.setItem(reMapSessionCacheKey(projectId), JSON.stringify(envelope));
    return true;
  } catch {
    // Session continuity is best-effort. A quota/security failure must never
    // interrupt reMap interaction or navigation.
    return false;
  }
}

export function clearReMapSessionCache(
  projectId: string,
  storage?: Storage,
): void {
  const target = availableStorage(storage);
  if (!target || !projectId) return;
  try {
    target.removeItem(reMapSessionCacheKey(projectId));
  } catch {
    // Storage can be disabled even after it was initially available.
  }
}

export function clearAllReMapSessionCaches(storage?: Storage): void {
  const target = availableStorage(storage);
  if (!target) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index);
      if (key?.startsWith(REMAP_SESSION_CACHE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => target.removeItem(key));
  } catch {
    // Logout must continue even when browser storage is unavailable.
  }
}

export function readReMapSessionCache(
  projectId: string,
  storage?: Storage,
): ParsedSavedGraphView | null {
  const target = availableStorage(storage);
  if (!target || !projectId) return null;
  const key = reMapSessionCacheKey(projectId);
  try {
    const serialized = target.getItem(key);
    if (!serialized) return null;
    const raw: unknown = JSON.parse(serialized);
    if (
      !isRecord(raw) ||
      raw.version !== REMAP_SESSION_CACHE_VERSION ||
      raw.projectId !== projectId ||
      !isRecord(raw.view) ||
      raw.view.schema_version !== GRAPH_VIEW_STATE_SCHEMA_VERSION ||
      typeof raw.view.view_mode !== 'string' ||
      !isRecord(raw.view.client_metadata)
    ) {
      target.removeItem(key);
      return null;
    }

    return parseSavedView({
      id: `session:${projectId}`,
      name: 'Last reMap session',
      description: null,
      view_mode: raw.view
        .view_mode as CaptureCurrentViewResult['view_mode'],
      plan_id: null,
      request_payload: null,
      client_metadata: raw.view.client_metadata,
      schema_version: raw.view.schema_version as number,
    });
  } catch {
    try {
      target.removeItem(key);
    } catch {
      // Ignore a second storage failure while invalidating corrupt data.
    }
    return null;
  }
}
